import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { translations, Language } from './translations';

// Default language
const DEFAULT_LANGUAGE: Language = 'fr';

// Get the current language from localStorage or use default
export const getCurrentLanguage = (): Language => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('easyfinder_language');
    if (stored && (stored === 'fr' || stored === 'en')) {
      return stored as Language;
    }
  }
  return DEFAULT_LANGUAGE;
};

// Set the current language
export const setCurrentLanguage = (lang: Language): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('easyfinder_language', lang);
    // notify same-tab listeners
    window.dispatchEvent(new Event('languagechange'));
  }
};

const subscribe = (onStoreChange: () => void) => {
  if (typeof window === 'undefined') return () => {};

  const handleStorage = (e: StorageEvent) => {
    if (e.key === 'easyfinder_language') onStoreChange();
  };

  window.addEventListener('languagechange', onStoreChange);
  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener('languagechange', onStoreChange);
    window.removeEventListener('storage', handleStorage);
  };
};

// Hook to use translations
export const useTranslation = () => {
  const lang = useSyncExternalStore(subscribe, getCurrentLanguage, () => DEFAULT_LANGUAGE);
  const t = useMemo(() => translations[lang], [lang]);

  const setLanguage = useCallback((newLang: Language) => {
    setCurrentLanguage(newLang);
  }, []);

  return { t, lang, setLanguage };
};

// Simple translation getter for non-hook contexts
export const getTranslation = () => {
  const lang = getCurrentLanguage();
  return translations[lang];
};
